import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import {
  fmtCurrency,
  fmtCurrencyCompact,
  fmtNumber,
  fmtNumberCompact,
  fmtPercent,
} from '../lib/format.ts';
import { KpiSkeleton } from '../components/Skeleton.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { Icon } from '../components/Icon.tsx';

interface Tile {
  label: string;
  value: string;
  sub: string;
  /** Optionale Farbe für den Wert (z. B. Storno-Warnung). */
  valueClass?: string;
  trend?: number[];
  trendColor?: string;
  trendFill?: string;
}

export function KpiTiles() {
  const filters = useFilterStore((s) => s.filters);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['kpis', filters],
    queryFn: () => api.kpis({ filters }),
  });
  // Sparkline-Daten: rolling Trend
  const tsQ = useQuery({
    queryKey: ['kpi-trend', filters],
    queryFn: () =>
      api.timeseries({
        filters,
        granularity: 'day',
        compareVorjahr: false,
      }),
  });

  if (isLoading) {
    return (
      <div className="kpi-band">
        <div className="kpi-keel" />
        <div className="kpi-grid">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="kpi-seg">
              <KpiSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const trendUmsatz = (tsQ.data ?? []).map((p) => p.umsatz);
  const trendBons = (tsQ.data ?? []).map((p) => p.anz_bons);

  const tiles: Tile[] = [
    {
      label: 'Umsatz',
      value: fmtCurrencyCompact(data.umsatz_gesamt),
      sub: fmtCurrency(data.umsatz_gesamt),
      trend: trendUmsatz,
      trendColor: '#0e7bd1',
      trendFill: 'rgba(14,123,209,0.12)',
    },
    {
      label: 'Bons',
      value: fmtNumberCompact(data.anz_bons),
      sub: fmtNumber(data.anz_bons),
      trend: trendBons,
      trendColor: '#64748b',
      trendFill: 'rgba(100,116,139,0.12)',
    },
    {
      label: 'Ø-Bon',
      value: fmtCurrency(data.avg_bon),
      sub: 'pro Bon (Brutto)',
    },
    {
      label: 'Filialen',
      value: fmtNumber(data.anz_filialen),
      sub: 'aktiv im Zeitraum',
    },
    {
      label: 'Storno-Quote',
      value: fmtPercent(data.storno_quote),
      sub: `${fmtNumber(data.anz_stornos)} Stornos`,
      valueClass:
        data.storno_quote > 0.01 ? 'text-accent-600 dark:text-accent-500' : undefined,
    },
  ];

  // Ein Band, fünf Segmente: die Kiellinie oben trägt die Mix-Markt-CI,
  // 1px-Fugen trennen die Messwerte — ein Instrument, keine Kachel-Wolke.
  return (
    <div className="kpi-band animate-fade-in">
      <div className="kpi-keel" />
      <div className="kpi-grid">
        {tiles.map((t) => (
          <div key={t.label} className="kpi-seg">
            <div className="kpi-seg-label">
              {t.label}
              {isFetching && !isLoading && (
                <Icon name="refresh" size={10} className="text-brand-500 animate-spin" />
              )}
            </div>
            <div
              className={`kpi-seg-value ${t.valueClass ?? ''}`}
              title={t.sub.length < 50 ? t.sub : undefined}
            >
              {t.value}
            </div>
            <div className="kpi-seg-sub">{t.sub}</div>
            {t.trend && t.trend.length > 1 ? (
              <div className="mt-1.5 opacity-70">
                <Sparkline data={t.trend} color={t.trendColor!} fillColor={t.trendFill} height={22} />
              </div>
            ) : (
              <div className="mt-1.5 h-[22px]" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
